import React, { useState, useEffect, useRef, useMemo } from 'react';
import DockingStation from '../client/ProfileAssistant/dockingstation';  // Add this import
import axios from 'axios';
import { resolveDisplayableWebsiteUrl } from '../../utils/displayableWebsiteUrl';
import { useParams, useNavigate } from 'react-router-dom';
import { getProfilePlaceholder } from '../../utils/profilePlaceholders';
import AllReviewsList from '../customers/AllReviewsList';
import DOMPurify from 'dompurify';
import { useProfileStore } from '../../state/profileStore';


function decodeEntities(encodedString) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = encodedString;
  return textarea.value;
}



function Profile() {
  const [profileUser, setProfileUser] = useState(null);
  const { slug: rawSlug } = useParams();
  const slug = rawSlug;
  const navigate = useNavigate();
  // Individual selectors (NOT a whole-store destructure) — Zustand's whole-
  // state subscription re-renders this component on every store update, which
  // combined with the refreshProfile→set()→re-render→refreshProfile loop in
  // the useEffect below caused 429s on /rating-summary after Brett saved the
  // Teams focus modal 2026-05-26. Individual selectors only re-render when
  // the SPECIFIC slice changes. Functions in the store closure are stable
  // references so their identity is constant across renders.
  const profile = useProfileStore((s) => s.profile);
  const setProfile = useProfileStore((s) => s.setProfile);
  const refreshProfile = useProfileStore((s) => s.refreshProfile);
  const [reviews, setReviews] = useState([]);
  const [socialPlatforms, setSocialPlatforms] = useState([]);
  const [, setIsLoadingReviews] = useState(false);
  const [ratingData, setRatingData] = useState({ u_total_review_score: 0, u_review_count: 0, u_avg_rating: null });
  const [visibleCount, setVisibleCount] = useState(10); // How many reviews to show initially
  const [isLoadingMore] = useState(false); // Loading state
  const sentinelRef = useRef(null); // Reference for infinite scroll detection
  const reviewsScrollRef = useRef(null); // Scroll container for the Reviews column
  const isFetchingRef = useRef(false); 
// In userprofile.jsx, update the fetchProfileBySlug function:
  const fetchProfileBySlug = async () => {
    // Clean incoming slug: strip leading numeric id and lowercase
    const cleanedSlug = decodeURIComponent(rawSlug || '')
      .replace(/^\d+[-_]/, '')
      .toLowerCase()
      .trim();

    // --- Hyphen-aware own-profile detection ---
    // Treat hyphens and spaces as equivalent *inside* name parts.
    const baseSlug = cleanedSlug.replace(/_\d+$/, ''); // ignore trailing _<number> (e.g., _2)
    const sForCompare = baseSlug.replace(/-/g, ' ');
    const parts = sForCompare.split('_'); // underscore remains the first/last delimiter
    let slugFirst = null, slugLast = null;
    if (parts.length >= 2) {
      slugLast = parts.pop().replace(/\s{2,}/g, ' ').trim();       // last name(s)
      slugFirst = parts.join(' ').replace(/\s{2,}/g, ' ').trim();  // first name(s)
    }

    const ownFirst = (profile?.u_first_name || '')
      .toLowerCase()
      .replace(/-/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const ownLast = (profile?.u_last_name || '')
      .toLowerCase()
      .replace(/-/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();

    // If viewing own profile (hyphen/space tolerant), skip API; use profile from store
    if (!rawSlug || (slugFirst && slugLast && slugFirst === ownFirst && slugLast === ownLast)) {
      if (profile) {
        setProfileUser(profile);
        if (profile.u_id) {
          fetchRatingSummary(profile.u_id);
          fetchReviews(profile.u_id);
        }
      }
      return;
    }
    // --- End own-profile detection ---

    // Otherwise fetch by slug exactly as provided (browser will encode spaces to %20)
    try {
      const response = await axios.get(
        `/api/users/profile/${cleanedSlug}`,
        { withCredentials: true }
      );
      setProfileUser(response.data);
      if (response.data?.u_id) {
        fetchRatingSummary(response.data.u_id);
        fetchReviews(response.data.u_id);
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      // Do NOT clear profileUser on error; prevents flicker/blank.
    }
  };



    useEffect(() => {
      if (rawSlug) {
        fetchProfileBySlug();
      } else if (profile?.u_id) {
        setProfileUser(profile);
        fetchRatingSummary(profile.u_id);
        fetchReviews(profile.u_id);
      }
    }, [rawSlug, profile?.u_id]);

    useEffect(() => {
      if (!rawSlug) return;

      const noIdSlug = rawSlug.replace(/^\d+[-_]/, '');
      const canonicalSlug = noIdSlug
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/-+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/^_+|_+$/g, '');

      // DON'T navigate when canonicalSlug is empty. The regex strips all
      // non-ASCII letters, so a Thai slug like ศิริพร_วงษ์สุวรรณ canonicalizes
      // to '' — which would navigate to /profile/, which would trigger the
      // OTHER useEffect (line ~300) to re-add the Thai slug, which would
      // canonicalize back to '', infinite loop. Chrome eventually throttled
      // it and the page errored out (Brett caught the URL flashing 2026-05-26).
      // For non-ASCII slugs, leave the URL as-is — there's no meaningful
      // ASCII-canonical form to navigate to.
      if (canonicalSlug && rawSlug !== canonicalSlug) {
        navigate(`/profile/${canonicalSlug}`, { replace: true });
      }
    }, [rawSlug, navigate]);

    // Keep profileUser live-updated when viewing your own profile
    useEffect(() => {
      if (!profile) return;

      const cleaned = decodeURIComponent(slug || '')
        .replace(/^\d+[-_]/, '')
        .toLowerCase()
        .trim();

      // Hyphen/space tolerant compare
      const baseSlug = cleaned.replace(/_\d+$/, ''); // ignore trailing _<number> (e.g., _2)
      const sForCompare = baseSlug.replace(/-/g, ' ');
      const parts = sForCompare.split('_');
      let slugFirst = null, slugLast = null;
      if (parts.length >= 2) {
        slugLast = parts.pop().replace(/\s{2,}/g, ' ').trim();
        slugFirst = parts.join(' ').replace(/\s{2,}/g, ' ').trim();
      }

      const ownFirst = (profile.u_first_name || '')
        .toLowerCase()
        .replace(/-/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
      const ownLast = (profile.u_last_name || '')
        .toLowerCase()
        .replace(/-/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();

      const isOwn = !slug || (slugFirst && slugLast && slugFirst === ownFirst && slugLast === ownLast);

      if (isOwn) {
        setProfileUser(profile);
      }
    }, [profile, slug]);

  // Fetch social platforms for the viewed user
  useEffect(() => {
    const uid = profileUser?.u_id;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/social/platforms/${uid}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (!cancelled && data.ok) setSocialPlatforms(data.platforms || []);
      } catch (err) {
        console.error('[userprofile] social platforms fetch error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [profileUser?.u_id]);


  console.log('💡 ProfileStore profile object:', profile);
  console.log('💡 ProfileStore business object:', profile?.business);
  console.log('Live profile data from ProfileStore:', profile);
  console.log('Live business phone:', profile?.b_phone);
  console.log('Live business address:', profile?.b_address);
  console.log('Live business industry:', profile?.b_business_industry);



    const fetchReviews = React.useCallback(async (userId) => {
    console.log('Fetching reviews for:', userId);
    if (!userId) return;

    try {
      const res = await axios.get(
        `/api/profile/${userId}/reviews`,
        { withCredentials: true }
      );
      
      // Only update if we get valid array data
      if (Array.isArray(res.data)) {
        setReviews(prevReviews => {
          // Only update if the data is actually different
          if (JSON.stringify(prevReviews) !== JSON.stringify(res.data)) {
            return res.data;
          }
          return prevReviews;
        });
      }
    } catch (err) {
      console.error('Review fetch error:', err);
      // Don't reset reviews here - maintain existing data
    }
  }, []);
  const fetchRatingSummary = async (userId) => {
    try {
      const response = await axios.get(
        `/api/profile/${userId}/rating-summary`,
        { withCredentials: true }
      );
      setRatingData({
        u_total_review_score: parseFloat(response.data.u_total_review_score),
        u_review_count: response.data.total_reviews || 0, // Key change: Use total_reviews
        u_avg_rating: parseFloat(response.data.u_avg_rating)
      });
    } catch (err) {
      console.error('Failed to load ratings:', err);
    }
  };
    // Usage (call when component mounts or user changes)
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    if (profileUser?.u_id) {
      const fetchData = async () => {
        try {
          setIsLoadingReviews(true);
          await fetchReviews();
          await fetchRatingSummary(profileUser.u_id);
        } catch (error) {
          if (isMounted) {
            console.error('Fetch error:', error);
            setReviews([]);
          }
        } finally {
          if (isMounted) {
            setIsLoadingReviews(false);
          }
        }
      };
      fetchData();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [profileUser?.u_id, fetchReviews]);

  useEffect(() => {
    const fetchFullProfile = async () => {
      if (profile?.u_id) {
        try {
          const response = await fetch(`/api/users/profile/${profile.u_id}`, {
            method: 'GET',
            credentials: 'include'
          });
          const updatedData = await response.json();
          if (updatedData && updatedData.u_id) {
            setProfile({
              ...updatedData,
              b_show_address: updatedData.b_show_address !== false
            });
          }
        } catch (err) {
          console.error('Failed to refresh profile data:', err);
        }
      }
    };

    fetchFullProfile();
  }, [profile?.u_id, setProfile]);

  useEffect(() => {
    if (profile?.u_id) {
      refreshProfile(profile.u_id);
    }
  }, [profile?.u_id, refreshProfile]);

  useEffect(() => {
    if (profile?.u_first_name && profile?.u_last_name && !slug) {
      const userSlug = generateProfileSlug(profile.u_first_name, profile.u_last_name);
      navigate(`/profile/${userSlug}`, { replace: true });
    }
    // Deps are PRIMITIVE fields, NOT the whole `profile` object. Zustand
    // creates a new profile reference on every set() call (even unrelated
    // store updates). With [profile, ...] as deps, this effect re-fired on
    // every store update — calling navigate() each time — until Chrome
    // throttled it with "Throttling navigation to prevent the browser from
    // hanging" warning, which silently killed subsequent nav clicks app-
    // wide. Brett caught this 2026-05-26 — the actual root cause of the
    // "click does nothing" symptom we've been hunting. Primitive deps
    // (strings) compare by value, so this effect now only fires when the
    // user's name actually changes, not on every render. `navigate` from
    // useNavigate is stable so it's safe to omit; eslint-disable to keep
    // the lint rule from re-adding the broken object dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.u_first_name, profile?.u_last_name, slug]);

  const safeUser = profile ?? {};
  const safeBiz  = profile?.business ?? {};
  const { businessPhone: displayBusinessPhone } = getProfilePlaceholder(safeUser, safeBiz);
  const { industry: displayIndustry }  = getProfilePlaceholder(safeUser, safeBiz);
  const { addressLine1: displayAddressLine1 } = getProfilePlaceholder(safeUser, safeBiz);
  const { addressLine2: displayAddressLine2 } = getProfilePlaceholder(safeUser, safeBiz);
  // Lean-onboarding Slice 3: render only when the user has explicitly answered.
  // Old code defaulted both to 'B2BC' when NULL, which shipped a false claim on
  // every brand-new profile (Brett caught this 2026-05-26 with the Thai PAS
  // signup — focus pill showed "B2C" though the user never picked anything).
  // null here means "don't render the pill at all"; the JSX below guards on
  // displayBusinessFocus / displayUserFocus existing.
  // Memoize the About Us decode + sanitize — pre-fix this was done inline as
  // an IIFE inside the JSX (around line 724) and re-ran on EVERY component
  // render. For Thai profiles where b_about_us is ~5KB of UTF-8 Thai text,
  // DOMPurify's parse is meaningfully expensive (multibyte chars take longer
  // to tokenize than ASCII). Combined with this component's many Zustand
  // subscriptions, the inline call was blocking the main thread on every
  // render → user clicks queued behind the parse work → "click does nothing,
  // second click works" symptom Brett kept hitting on Thai profiles.
  // companyprofile.jsx already memoizes this; userprofile just hadn't been
  // updated to match. Brett finally pinpointed it 2026-05-26.
  const aboutUsHTML = useMemo(() => {
    const raw = profileUser?.about || profileUser?.b_about_us;
    if (!raw) return null;
    return DOMPurify.sanitize(decodeEntities(raw));
  }, [profileUser?.about, profileUser?.b_about_us]);

  const displayBusinessFocus = profileUser?.b_customer_focus?.trim() || null;
  const displayUserFocus = profileUser?.u_customer_focus?.trim() || null;
  const [userTooltip, setUserTooltip] = useState('Loading...');
  const [businessTooltip, setBusinessTooltip] = useState('Loading...');

  useEffect(() => {
    setReviews([]); // Clear old reviews immediately
    setIsLoadingReviews(true);
  }, [profile?.u_id]);


  useEffect(() => {
    const node = sentinelRef.current;
    const rootEl = reviewsScrollRef.current;
    if (!node || !rootEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          !isFetchingRef.current &&
          visibleCount < reviews.length
        ) {
          isFetchingRef.current = true;

          setVisibleCount((prev) => {
            const remaining = reviews.length - prev;
            return prev + Math.min(10, remaining);
          });

          setTimeout(() => {
            isFetchingRef.current = false;
          }, 300);
        }
      },
      {
        root: rootEl,
        rootMargin: '200px 0px',
        threshold: 0.01
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [visibleCount, reviews.length]);







  useEffect(() => {
    const fetchTooltips = async () => {
      try {
        const userFocus = profileUser.u_customer_focus;
        const businessFocus = profileUser.b_customer_focus;

        // Fetch tooltips
      const userResponse = await axios.get(`/api/tooltips/associate/${userFocus}?warning=false`);
      const businessResponse = await axios.get(`/api/tooltips/business/${businessFocus}?warning=false`);

        // Personalize the user tooltip
        setUserTooltip(`${profileUser.u_first_name} represents ${profileUser.b_name}, and ${userResponse.data?.t_template || ''}`);
        
        // Personalize the business tooltip
        setBusinessTooltip(`${profileUser.b_name} ${businessResponse.data?.t_template || ''}`);

      } catch (error) {
        console.error('Tooltip fetch error:', error);
        setUserTooltip('Error loading tooltip');
        setBusinessTooltip('Error loading tooltip');
      }
    };

    if (profileUser) {
      fetchTooltips();
    }
  }, [profileUser]);

  function generateProfileSlug(firstName, lastName) {
    if (!firstName || !lastName) return '';
    return `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
  };

  if (!profile) {
    return <div className="container mx-auto w-4/5 p-6">Loading profile...</div>;
  }

  // Add this function to check if the profile belongs to the logged-in user
  const isOwnProfile = () => {
    if (!profile || !profileUser) return false;
    return profile.u_id === profileUser.u_id;
  };

  // Temporary debug - remove after fixing
  console.log('Rating data:', ratingData);
  console.log('API URL:', process.env.REACT_APP_API_URL);
  return (
    <div className="container mx-auto w-4/5 pt-6 flex flex-col min-h-screen">
      {/* Add DockingStation here */}
      {isOwnProfile() && <DockingStation isOwner={true} />}
      
<div className="grid grid-cols-1 md:grid-cols-[24fr_48fr_28fr] gap-3 flex-grow">

        {/* Left Column - User Info */}
        <div className="min-w-0 box-border bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] h-fit">
          <div className="space-y-4">

            {/* === PROFILE CARD: Photo + Name + Title === */}
            <div
              className="rounded-lg p-4 bg-white transition-all duration-200"
              style={{
                boxShadow: '0 4px 12px -2px rgba(0, 174, 247, 0.25), 0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -8px 12px -8px rgba(0, 174, 247, 0.1)',
                border: '1px solid rgba(0, 174, 247, 0.2)',
                borderBottomColor: 'rgba(0, 174, 247, 0.35)',
                borderBottomWidth: '2px'
              }}
            >
              {/* User Profile Picture */}
              <div className="relative w-28 h-28 mx-auto mb-3">
                <div className="w-28 h-28 rounded-full border-2 border-[#00aef7]/30 overflow-hidden">
                  <img
                    src={
                      profileUser?.u_profile_picture
                        ? `${process.env.REACT_APP_API_URL}${profileUser.u_profile_picture}`
                        : '/images/default-avatar.png'
                    }
                    className="w-full h-full object-cover"
                    alt="Profile"
                  />
                </div>

                {/* Customer Focus Badge - matches column 2 styling.
                    Slice 3: hide entirely when u_customer_focus is unset. */}
                {displayUserFocus && (
                  <div className="absolute -bottom-2 -right-6">
                    <div className="relative group">
                      <div className="text-sm font-bold bg-[#00aef7]/10 px-2 py-0.5 rounded-full border border-[#00aef7]/20 text-[#00aef7] cursor-help">
                        {displayUserFocus}
                      </div>
                      <div className="hidden group-hover:block absolute z-10 bg-black text-white text-xs p-2 rounded-md w-80 -translate-x-1/2 left-1/2 mt-6">
                        {userTooltip}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Name & Title */}
              <h2 className="text-lg font-bold text-gray-800 text-center">
                {profileUser?.u_first_name} {profileUser?.u_last_name}
              </h2>
              <p className="text-sm text-gray-600 text-center">{profileUser?.u_job_title}</p>
            </div>

            {/* === CONTACT CARD: Phone + Email === */}
            <div
              className="rounded-lg p-3 bg-white transition-all duration-200"
              style={{
                boxShadow: '0 2px 6px -1px rgba(156, 163, 175, 0.15), 0 1px 2px -1px rgba(156, 163, 175, 0.1), inset 0 -4px 6px -4px rgba(156, 163, 175, 0.08)',
                border: '1px solid rgba(156, 163, 175, 0.2)',
                borderBottomColor: 'rgba(156, 163, 175, 0.3)',
                borderBottomWidth: '2px'
              }}
            >
              {/* Phone */}
              <a
                href={`tel:${profileUser?.u_phone_number}`}
                className="flex items-center gap-2 text-xs text-gray-700 hover:text-[#00aef7] transition-colors mb-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#00aef7">
                  <path d="M280-40q-33 0-56.5-23.5T200-120v-720q0-33 23.5-56.5T280-920h400q33 0 56.5 23.5T760-840v124q18 7 29 22t11 34v80q0 19-11 34t-29 22v404q0 33-23.5 56.5T680-40H280Zm0-80h400v-720H280v720Zm40-240h320L535-500l-75 100-55-73-85 113Zm308.5-211.5Q640-583 640-600t-11.5-28.5Q617-640 600-640t-28.5 11.5Q560-617 560-600t11.5 28.5Q583-560 600-560t28.5-11.5ZM280-120v-720 720Z"/>
                </svg>
                <span>{profileUser?.u_phone_number || 'Phone not available'}</span>
              </a>

              {/* Email */}
              <a
                href={`mailto:${profileUser?.u_email}`}
                className="flex items-center gap-2 text-xs text-gray-700 hover:text-[#00aef7] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#00aef7">
                  <path d="M280-280q-33 0-56.5-23.5T200-360v-400q0-33 23.5-56.5T280-840h560q33 0 56.5 23.5T920-760v400q0 33-23.5 56.5T840-280H280Zm280-188L280-663v303h560v-303L560-468Zm0-98 280-194H280l280 194ZM120-120q-33 0-56.5-23.5T40-200v-500h80v500h660v80H120Zm720-546v-94H280v94-94h560v94Z"/>
                </svg>
                <span className="truncate">{profileUser?.u_email || 'Email not available'}</span>
              </a>
            </div>

            {/* === RATINGS CARD === */}
            <div
              className="rounded-lg p-0 bg-white transition-all duration-200"
              style={{
                boxShadow: '0 2px 6px -1px rgba(156, 163, 175, 0.15), 0 1px 2px -1px rgba(156, 163, 175, 0.1), inset 0 -4px 6px -4px rgba(156, 163, 175, 0.08)',
                border: '1px solid rgba(156, 163, 175, 0.2)',
                borderBottomColor: 'rgba(156, 163, 175, 0.3)',
                borderBottomWidth: '2px'
              }}
            >
              <p className="text-l text-[#00aef7] font-bold text-center mt-0 mb-0">Ratings & Reviews</p>
              {ratingData.u_avg_rating ? (
                <div className="text-center">
                  <div className="flex justify-center text-yellow-400 text-xl mb-0">
                    {'★'.repeat(Math.round(ratingData.u_avg_rating))}
                    {'☆'.repeat(5 - Math.round(ratingData.u_avg_rating))}
                  </div>
                  <p className="text-lg font-bold text-gray-800">
                    {ratingData.u_avg_rating.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {ratingData.u_review_count} review{ratingData.u_review_count > 1 ? 's' : ''}
                  </p>
                </div>
              ) : (
                <p className="text-center text-sm text-gray-500">No reviews yet</p>
              )}
            </div>

            {/* === SOCIAL MEDIA CARD === */}
            <div
              className="rounded-lg p-3 bg-white transition-all duration-200"
              style={{
                boxShadow: '0 2px 6px -1px rgba(156, 163, 175, 0.15), 0 1px 2px -1px rgba(156, 163, 175, 0.1), inset 0 -4px 6px -4px rgba(156, 163, 175, 0.08)',
                border: '1px solid rgba(156, 163, 175, 0.2)',
                borderBottomColor: 'rgba(156, 163, 175, 0.3)',
                borderBottomWidth: '2px'
              }}
            >
              <p className="text-xs text-gray-500 text-center mb-2">Social Channels</p>
              {/* First Row - 3 icons */}
              <div className="flex justify-center space-x-3 mb-2">
                {/* Facebook */}
                <svg className="w-8 h-8 transition-colors" fill={socialPlatforms.includes('facebook') ? '#1877F2' : '#9ca3af'} viewBox="0 0 24 24">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                </svg>

                {/* X/Twitter */}
                <svg className="w-8 h-8 transition-colors" fill={socialPlatforms.includes('twitter') ? '#000000' : '#9ca3af'} viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>

                {/* YouTube */}
                <svg className="w-8 h-8 transition-colors" fill={socialPlatforms.includes('youtube') ? '#FF0000' : '#9ca3af'} viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>

              {/* Second Row - 3 icons */}
              <div className="flex justify-center space-x-3">
                {/* LinkedIn */}
                <svg className="w-8 h-8 transition-colors" fill={socialPlatforms.includes('linkedin') ? '#0A66C2' : '#9ca3af'} viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>

                {/* TikTok */}
                <svg className="w-8 h-8 transition-colors" fill={socialPlatforms.includes('tiktok') ? '#000000' : '#9ca3af'} viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>

                {/* Instagram */}
                <svg className="w-8 h-8 transition-colors" fill={socialPlatforms.includes('instagram') ? '#E4405F' : '#9ca3af'} viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>

                {/* WhatsApp — first-share rule (gray until first share, then green) */}
                <svg className="w-8 h-8 transition-colors" fill={socialPlatforms.includes('whatsapp') ? '#25D366' : '#9ca3af'} viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                </svg>
              </div>
            </div>

          </div>
        </div>

        {/* Middle Column - Business Info (match companyprofile.jsx spacing) */}
<div className="min-w-0 box-border bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col h-[calc(100vh-7rem)]">

          {/* === BUSINESS HEADER CARD: Name, Contact, Logo, Industry === */}
          <div
            className="rounded-lg p-3 mb-3 bg-white"
            style={{
              boxShadow: '0 2px 6px -1px rgba(0, 174, 247, 0.12), 0 1px 2px -1px rgba(0, 174, 247, 0.08), inset 0 -4px 6px -4px rgba(0, 174, 247, 0.05)',
              border: '1px solid rgba(0, 174, 247, 0.15)',
              borderBottomColor: 'rgba(0, 174, 247, 0.2)',
              borderBottomWidth: '2px'
            }}
          >
            {/* Business Name */}
            <div className="w-full mb-0">
              <h1
                className="text-2xl font-bold break-words text-[#00aef7] leading-tight cursor-pointer"
                onClick={() => {
                  // Hybrid routing — see navmenu.jsx click handler for the
                  // full rationale. TL;DR: clean website hosts go through the
                  // friendly slug route; platform URLs (with a path beyond
                  // the domain) go through the stable by-id route to avoid
                  // slash-in-slug + percent-encoding issues.
                  const cleaned = (profileUser?.b_website || '')
                    .replace(/^https?:\/\//, '').replace(/\/+$/, '');
                  const isPlatformUrl = cleaned.includes('/');
                  if (isPlatformUrl && profileUser?.b_id) {
                    navigate(`/company/by-id/${profileUser.b_id}`);
                    return;
                  }
                  if (cleaned) navigate(`/company/${cleaned}`);
                }}
              >
                {profileUser?.b_name || 'Loading Business...'}
              </h1>
            </div>

            {/* Contact Info and Logo Section */}
            <div className="flex justify-between items-start">
              {/* Left Side - Contact Info */}
              <div className="flex-1 pt-1.5">
                {profileUser?.b_show_address !== false && (
                  <div style={{ minHeight: '60px' }}>
                    <p className="text-sm text-gray-600 leading-none">
                    </p>
                    <div style={{ minHeight: '60px' }}>
                      {/* Street Address */}
                      <p className="text-sm text-gray-600 leading-none">
                        {profileUser?.b_address?.trim() ? profileUser.b_address : displayAddressLine1}
                      </p>
                      {/* City, State Zip */}
                      <p className="text-sm text-gray-600 leading-normal mb-3">
                        {(profileUser?.b_city || profileUser?.b_state || profileUser?.b_zip_code)
                          ? `${profileUser.b_city || ''}, ${profileUser.b_state || ''} ${profileUser.b_zip_code || ''}`
                          : displayAddressLine2}
                      </p>
                    </div>
                  </div>
                )}

                <p className="text-sm text-gray-600 leading-none">
                  <a href={`tel:${profileUser?.b_phone?.trim() || ''}`} className="hover:text-blue-600">
                    {profileUser?.b_phone?.trim() || displayBusinessPhone}
                  </a>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <a
                    href={resolveDisplayableWebsiteUrl(profileUser?.b_website, profileUser?.b_place_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00aef7] hover:text-blue-600"
                  >
                    Visit our Website
                  </a>
                </p>
              </div>
              {/* Right Side - Logo and Badge */}
              <div className="w-[54%] relative pt-4">
                <div className="relative w-full h-24 max-w-[280px] mx-auto group">
                {profileUser?.b_logo ? (
                  <img
                    src={`${process.env.REACT_APP_API_URL}${profileUser.b_logo}`}
                    alt="Business Logo"
                    className="w-full h-full object-contain bg-white"
                  />
                ) : (
                  <>
                    <img
                      src="/images/Logo_Placeholder.jpg"
                      alt="Logo Placeholder"
                      className="w-full h-full object-contain opacity-50 border-2 border-[#00aef7]/30 rounded-md"
                    />
                  </>
                )}
                </div>

                {/* Business Industry - Now inside logo container */}
                <div className="mt-0 text-center">
                  <p className="text-lg font-bold text-gray-600">
                    {profileUser?.b_business_industry?.trim() || displayIndustry}
                  </p>
                </div>

                {/* Badge code remains here.
                    Slice 3: hide entirely when b_customer_focus is unset. */}
                {displayBusinessFocus && (
                  <div className="absolute -top-[12px] -right-1 z-30">
                    <div className="relative group">
                      <div className="text-sm font-bold bg-[#00aef7]/10 px-2 py-0.5 rounded-full border border-[#00aef7]/20 text-[#00aef7] cursor-help">
                        {displayBusinessFocus}
                      </div>
                      <div className="hidden group-hover:block absolute z-20 bg-black text-white text-xs p-2 rounded-md w-80 -translate-x-1/2 left-1/2 mt-6">
                        {businessTooltip}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* === ABOUT US SECTION === */}
          {(profileUser?.about || profileUser?.b_about_us) ? (
            <div
              className="flex-1 overflow-y-auto rounded-lg scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent"
              style={{
                backgroundColor: '#ffffff',
                boxShadow: 'inset 0 2px 8px -2px rgba(0, 0, 0, 0.08), inset 0 0 0 1px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }}
            >
              {aboutUsHTML && (
                <div
                  className="w-full h-full p-4 text-gray-700"
                  style={{ overflowY: 'visible' }}
                  dangerouslySetInnerHTML={{ __html: aboutUsHTML }}
                />
              )}
            </div>
          ) : null}

        </div>

  {/* Right Column - Reviews & Ratings (match companyprofile.jsx spacing) */}
  <div className="min-w-0 box-border bg-white rounded-lg p-4 h-[calc(100vh-7rem)] shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col">
    <div className="top-0 bg-white pb-2 z-10">
      <h2 className="text-xl font-bold text-[#00aef7] mb-2 text-center">
        Ratings & Reviews
      </h2>
    </div>

    <div
      ref={reviewsScrollRef}
      className="relative flex-1 min-h-0 overflow-y-auto pl-3 pr-2 rounded-lg scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent"
    >
      <AllReviewsList
        reviews={reviews.slice(0, visibleCount)}
        showReplyStatus={true}
        hideControls={true}
      />

      {/* Inline loading indicator */}
      {isLoadingMore && visibleCount < reviews.length && (
        <div className="flex items-center justify-center py-3">
          <div className="h-5 w-5 animate-spin rounded-full border border-gray-300 border-t-transparent"></div>
          <span className="ml-2 text-sm text-gray-500">Loading...</span>
        </div>
      )}

      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  </div>




      </div>
      {/* Footer Section */}
      <footer className="mt-6"> {/* Adjust margin to control gap */}
        {/* Footer content here */}
      </footer>
    </div>
  );
}

export default Profile;
